/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import {
  FlexRow,
  type RenderedDimensions,
} from '@lib/canvasx/components/FlexRow';
import { Text } from '@lib/canvasx/components/Text';
import { canvasCssValue } from '@lib/canvasx/util';
import shapeAhaRecord from '../assets/icons/shapeAhaRecord.svg';
import shapeRecordIcon from '../assets/icons/shapeRecordIcon.svg';
import { CardContents } from '../canvasx/CardContents';
import {
  type CardGroupContent,
  generateCardDataForRecord,
  type CardData,
} from '../canvasx/fieldContents';
import RecordCard from '../canvasx/RecordCard';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import LinearResizeControl from '../controls/LinearResizeControl';
import { type ToolList } from '../fabric/ObjectBehaviors';
import type { WhiteboardRecordInfo } from '../shared-types';
import tools from '../tools';
import { svgToIcon } from '../utils/svg';
import RecordDecorator, {
  RECORD_FONT_SIZE,
  RECORD_LINE_HEIGHT,
  RECORD_TITLE_FONT_SIZE,
} from './RecordDecorator';
import ShapeBase from './ShapeBase';
import { normalizeRecordType } from '../utils/record';
import ToolOpenRecord from '../tools/ToolOpenRecord';
import HighlightCard from '../canvasx/HighlightCard';

import {
  RECORD_MINIMUM_WIDTH,
  RECORD_MINIMUM_HEIGHT,
  RECORD_DEFAULT_WIDTH,
  RECORD_DEFAULT_HEIGHT,
} from '../constants';

class ShapeRecord extends ShapeBase {
  _sanitizedText?: string;
  // Saves the layout for the rendered record in order to resize the card to fit
  renderedDimensions?: RenderedDimensions;

  constructor(fabricObject: ShapeBase['fabricObject']) {
    super(fabricObject);

    this.fabricObject.uniformScaling = true;
    this.fabricObject.strokeWidth = 0;
    this.fabricObject.opacity = 1;
    this.fabricObject.stroke = undefined;

    if (!this.fabricObject.width) {
      this.fabricObject.set('width', RECORD_DEFAULT_WIDTH);
    } else if (this.fabricObject.width < RECORD_MINIMUM_WIDTH) {
      this.fabricObject.set('width', RECORD_MINIMUM_WIDTH);
    }

    if (!this.fabricObject.height) {
      this.fabricObject.set('height', RECORD_DEFAULT_HEIGHT);
    } else if (this.fabricObject.height < RECORD_MINIMUM_HEIGHT) {
      this.fabricObject.set('height', RECORD_MINIMUM_HEIGHT);
    }

    // Force the record to have consistent scale.
    if (this.scaleX !== this.scaleY) {
      this.fabricObject.set('scaleX', this.fabricObject.scaleY);
    }

    this.fabricObject.enableFrameBehaviors();
  }

  static renderMenuIcon() {
    return svgToIcon(shapeAhaRecord);
  }

  static renderIcon(): JSX.Element {
    return svgToIcon(shapeRecordIcon);
  }

  static iconClass() {
    return 'fa-regular fa-credit-card-front';
  }

  static defaultWidth() {
    return RECORD_DEFAULT_WIDTH;
  }

  static defaultHeight() {
    // Also change in app/extras/whiteboards/builder.rb
    return RECORD_DEFAULT_HEIGHT;
  }

  static getDescription(): string {
    return 'Existing';
  }

  static shapeControls = this.createShapeControls();
  static createShapeControls() {
    const controls = this.createDefaultControls();

    controls.mr = new LinearResizeControl({ edge: 'right' });
    controls.ml = new LinearResizeControl({ edge: 'left' });

    // NOTE the default controls don't show due to their checking uniformScaling, but
    // LinearResizeControl does not perform that check
    controls.mt = new LinearResizeControl({ edge: 'top' });
    controls.mb = new LinearResizeControl({ edge: 'bottom' });

    return controls;
  }

  // Override in the subclass, currently only used by ShapeRecord
  minDimension({ resizingWidth = true }) {
    return resizingWidth ? RECORD_MINIMUM_WIDTH : RECORD_MINIMUM_HEIGHT;
  }

  maxDimension() {
    return Number.MAX_SAFE_INTEGER;
  }

  hasExternalContent() {
    return false;
  }

  hasEmbeddedText() {
    return false;
  }

  get description(): string {
    return this.recordInfo?.name ?? '';
  }

  allowedStyleCopyPasteFields(): string[] {
    return [];
  }

  tools() {
    return [
      [tools.ToolShapeType],
      [tools.ToolOpenRecord],
      [tools.ToolCardSizeToFit],
      [tools.ToolCardLayout],
    ];
  }

  textTools(): ToolList {
    return [];
  }

  get shouldRenderText(): boolean {
    return false;
  }

  get sanitizedText() {
    return (this._sanitizedText ||= this.fabricObject.plainText());
  }

  saveRenderedDimensions(dimensions: RenderedDimensions) {
    this.renderedDimensions = dimensions;
  }

  sizeToFit() {
    const height = this.renderedDimensions?.height;

    if (height) {
      this.fabricObject.set('height', height);
    }
  }

  wasSet(keyOrPath: KeyPath, value: unknown): void {
    super.wasSet(keyOrPath, value);

    if (keyOrPath === 'textContent') {
      this._sanitizedText = undefined;

      // Manually re-initializing the text
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.sanitizedText;
    }
  }

  updateCardData(cardData: CardData) {
    if (
      !this.fabricObject.cardData ||
      JSON.stringify(this.fabricObject.cardData) !== JSON.stringify(cardData)
    ) {
      this.set('cardData', cardData);
    }
  }

  openRecord = () => {
    const { controller } = this.fabricObject;
    ToolOpenRecord.action({ selection: this.fabricObject, controller });
  };

  updateRecordProps(record: WhiteboardRecordInfo): void {
    super.updateRecordProps(record);

    this.buildCardData(record);
  }

  getCardData(record: WhiteboardRecordInfo): CardData | undefined {
    const { cardData } = this.fabricObject;
    if (cardData) {
      this.normalizeCardData(cardData);
      return cardData;
    }

    return this.buildCardData(record);
  }

  // If the record has been loaded, grabs the card view for that record, uses
  // that to generate the cardData which is then store back to this object.
  buildCardData(record: WhiteboardRecordInfo): CardData | undefined {
    const recordLoaded =
      record.typename !== 'Undefined' && record.typename !== 'NotFound';
    if (!recordLoaded) {
      return;
    }

    const { cardLayoutManager } = this.fabricObject.controller;
    const cardView = cardLayoutManager.cardViewForRecord(record);

    if (!cardView) {
      return;
    }

    const cardData = generateCardDataForRecord(record, cardView);

    if (cardData) {
      this.updateCardData(cardData);
    }

    return cardData;
  }

  isCardGroupContentArray(
    content: any
  ): content is [CardGroupContent, CardGroupContent] {
    return Array.isArray(content) && content.length === 2;
  }

  // Backwards compatibility. We made CardRowContent more flexible, and this
  // ensures that the previous cardData.content conforms to the new format.
  normalizeCardData(cardData: CardData) {
    const { content } = cardData;
    cardData.content = content.map(row => {
      if (this.isCardGroupContentArray(row)) {
        return { type: 'content', content: row };
      }

      return row;
    });
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { width, height, opacity } = this.fabricObject;

    const x = -width / 2;
    const y = -height / 2;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(1 / this.scaleX, 1 / this.scaleY);

    withContext(
      {
        canvas: this.canvas,
        ctx,
        object: this.fabricObject,
        // This assumes records have the same X/Y scale
        scale: this.scaleX,
      },
      () => {
        const record: WhiteboardRecordInfo = this.recordInfo || {
          typename: 'Undefined',
        };

        const cardData = this.getCardData(record);

        if (cardData) {
          renderElement(
            <CardContents
              cardData={cardData}
              width={width}
              height={height}
              setRenderedDimensions={dim => this.saveRenderedDimensions(dim)}
              opacity={opacity}
            />
          );
        } else {
          // We don't have card data when they first paste the record. In which
          // case you probably have { typename: 'Unknown' } as the record.
          const recordTypename = normalizeRecordType(
            this.fabricObject.recordType!
          );
          const decoratedRecord = new RecordDecorator(record, recordTypename);
          const recordLoaded =
            record.typename !== 'Undefined' && record.typename !== 'NotFound';

          // Disable the hover if we have an active transform
          const disableHover = !!this.fabricObject.canvas._currentTransform;
          const hoveringName =
            this.fabricObject.hoveredKey === 'name' && !disableHover;

          if (
            window.featureFlags?.ad_349_discovery_annotations_in_whiteboards &&
            record.typename === 'Annotation'
          ) {
            renderElement(
              <HighlightCard
                width={width}
                height={height}
                setRenderedDimensions={dim => this.saveRenderedDimensions(dim)}
                record={record}
                sanitizedText={this.sanitizedText}
                onClick={recordLoaded ? this.openRecord : undefined}
              />
            );
            return;
          }

          renderElement(
            <RecordCard
              highlightColor={'#e1e1e1'}
              width={width}
              height={height}
              setRenderedDimensions={dim => this.saveRenderedDimensions(dim)}
              opacity={opacity}
            >
              <FlexRow verticalAlignment='center' gap={7}>
                {record.typename === 'Undefined'
                  ? RecordDecorator.defaultIconForType(recordTypename)
                  : decoratedRecord.icon}
                <Text
                  lineHeight={RECORD_LINE_HEIGHT}
                  size={RECORD_FONT_SIZE}
                  flexGrow
                >
                  {decoratedRecord.identifier}
                </Text>

                {decoratedRecord.meta}
              </FlexRow>

              <FlexRow
                gap={{ horizontal: 4, vertical: 0 }}
                maxRows={2}
                verticalAlignment='center'
              >
                <Text
                  key='name'
                  color={
                    hoveringName && recordLoaded
                      ? canvasCssValue('--theme-link-text', '#0073cf')
                      : canvasCssValue('--theme-primary-text', '#333333')
                  }
                  lineHeight={1.4}
                  size={RECORD_TITLE_FONT_SIZE}
                  onClick={recordLoaded ? this.openRecord : undefined}
                >
                  {this.sanitizedText || 'Unknown'}
                </Text>
              </FlexRow>
            </RecordCard>
          );
        }
      }
    );

    ctx.restore();
  }
}

export default ShapeRecord;
